<template>
  <v-container v-if="prediction">
    <toolbar mode="prediction" :item="prediction" @clone="onClone">
      <template v-if="isOwner(prediction)">
        <regenerate @confirmed="onRegenerate" />
        <ax-button icon="mdi-download" tooltip="Download" @clicked="onDownload" />
        <export :prediction="prediction" @submitted="onExport" />
      </template>
    </toolbar>
    <v-card>
      <v-tabs v-model="tab">
        <template v-if="prediction.status == 'SUCCESS'">
          <v-tab href="#data">Data</v-tab>
          <v-tab href="#info">Info</v-tab>
          <v-tab href="#exports">Exports</v-tab>
        </template>
        <v-tab v-if="result" href="#result">Result</v-tab>
        <v-tab href="#details">Details</v-tab>
      </v-tabs>
      <v-divider></v-divider>
      <v-tabs-items v-model="tab">
        <template v-if="prediction.status == 'SUCCESS'">
          <v-tab-item value="data">
            <frame-head :items="head" />
          </v-tab-item>
          <v-tab-item value="info">
            <frame-info :items="info" />
          </v-tab-item>
          <v-tab-item value="exports">
            <v-data-table
              :items="prediction.exports"
              :headers="headers"
              disable-pagination
              disable-sort hide-default-footer
              :items-per-page="-1"
            >

              <template v-slot:[`item.status`]="{ item }">
                <ax-status :uuid="item.uuid" :status="item.status" />
              </template>
              <template v-slot:[`item.config`]="{ item }">
                {{ item.config }}
                <div v-html="exportConfig(item)"></div>
              </template>
              <template v-slot:[`item.updated_at`]="{ item }">
                <span :title="item.updated_at | dateFormat('MMM d, YYYY')">{{ item.updated_at | dateFormat }}</span>
              </template>
              <template v-slot:item.actions="{ item }">
                <template v-if="item.status == 'SUCCESS'">
                  <ax-button tooltip="Download Export" icon="mdi-download" @clicked="onSaveExport(item)" />
                  <ax-button confirm tooltip="Refresh Export" icon="mdi-refresh" @confirmed="onRefreshExport(item)" />
                  <ax-button confirm tooltip="Delete Export" icon="mdi-trash-can-outline" danger @confirmed="onDeleteExport(item)" />
                </template>
                <template v-else-if="['PENDING','STARTED'].includes(item.status)">
                  <ax-button confirm tooltip="Cancel Export" icon="mdi-cancel" danger @confirmed="onCancelExport(item)" />
                </template>
                <template v-else-if="['REVOKED','FAILURE'].includes(item.status)">
                  <ax-button confirm tooltip="Refresh Export" icon="mdi-refresh" @confirmed="onRefreshExport(item)" />
                  <ax-button confirm tooltip="Delete Export" icon="mdi-trash-can-outline" danger @confirmed="onDeleteExport(item)" />
                </template>
              </template>
            </v-data-table>
          </v-tab-item>
        </template>
        <v-tab-item v-if="result" value="result">
          <v-card-text>
            <p>{{ errors }}</p>
            <pre v-if="isSuper">{{ result.traceback }}</pre>
          </v-card-text>
        </v-tab-item>
        <v-tab-item value="details">
          <v-card-text>
            <v-container fluid class="pa-0">
              <v-row>
                <v-col>
                  <v-row dense>
                    <v-col cols="12" sm="2" md="1" class="font-weight-bold">Application</v-col>
                    <v-col>
                      <router-link :to="{ name: 'applicationShow', params: { slug: prediction.application.slug } }">
                        {{ prediction.application.name }}
                      </router-link>
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col cols="12" sm="2" md="1" class="font-weight-bold">Dataset</v-col>
                    <v-col>
                      <router-link :to="{ name: 'datasetShow', params: { uuid: prediction.dataset.uuid } }">
                        {{ prediction.dataset.name }}
                      </router-link>
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col cols="12" sm="2" md="1" class="font-weight-bold">Methods</v-col>
                    <v-col>
                      {{ prediction.methods.map(a => a.name).join(", ") }}
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col cols="12" sm="2" md="1" class="font-weight-bold">Settings</v-col>
                    <v-col>
                      <a class="text-decoration-underline" @click="settings = !settings">{{ settings ? 'Hide' : 'Show' }} Settings</a>
                      <pre v-if="settings">{{ prediction.config }}</pre>
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col cols="12" sm="2" md="1" class="font-weight-bold">Owner</v-col>
                    <v-col>{{ prediction.user.username }}</v-col>
                  </v-row>
                  <v-row dense>
                    <v-col cols="12" sm="2" md="1" class="font-weight-bold">Exported</v-col>
                    <v-col>
                      <span v-if="prediction.exported_at">{{ prediction.exported_at | dateFormat }}</span>
                      <span v-else>Never</span>
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col cols="12" sm="2" md="1" class="font-weight-bold">Created</v-col>
                    <v-col>{{ prediction.created_at | dateFormat }}</v-col>
                  </v-row>
                  <v-row dense>
                    <v-col cols="12" sm="2" md="1" class="font-weight-bold">Updated</v-col>
                    <v-col>{{ prediction.updated_at | dateFormat }}</v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </v-container>
</template>

<script>
import toolbar from "@/components/toolbar"
import AxButton from "@/components/button"
import AxStatus from "@/components/status"
import frameHead from "@/components/head"
import frameInfo from "@/components/info"
import Regenerate from "@/components/regenerate"
import Predictions from "@/api/predictions"
import Exports from "@/api/exports"
export default {
  name: "Prediction",
  components: {
    toolbar,
    AxButton,
    AxStatus,
    frameHead,
    frameInfo,
    Regenerate,
  },
  data() {
    return {
      // tab: "data",
      prediction: null,
      head: {
        data: [],
        schema: {}
      },
      info: {
        data: [],
        schema: {}
      },
      headers: [
        { text: 'Status', value: 'status', width: 1, align: "center" },
        { text: 'Settings', value: 'settings' },
        { text: 'Generated', value: 'updated_at', width: 1, cellClass: "text-no-wrap" },
        { text: 'Actions', value: 'actions', width: 1, cellClass: "text-no-wrap", align: "center" },
      ],
      settings: false
    }
  },
  created() {
    Predictions.retrieve(this.$route.params.uuid).then(response => this.prediction = response.data)
    Predictions.head(this.$route.params.uuid).then(response => (this.head = typeof response.data == "string" ? JSON.parse(response.data) : response.data))
    Predictions.info(this.$route.params.uuid).then(response => (this.info = typeof response.data == "string" ? JSON.parse(response.data) : response.data))
  },
  methods: {
    onRegenerate(refresh) {
      let formData = {
        name: this.prediction.name,
        application: this.prediction.application.id,
        methods: this.prediction.methods.map(m => m.id),
        dataset: this.prediction.dataset.id,
        config: this.prediction.config,
        user: this.user.id,
        refresh: refresh
      }
      Predictions.update(this.$route.params.uuid, formData).then(_ => {
        this.notify("Generating prediction")
        this.$router.push({ name: "predictionList" })
      })
    },
    onSelect(connection, table) {
      this.exporting.connection = connection
      this.exporting.table = table
    },
    onDownload() {
      Predictions.download(this.$route.params.uuid).then(response => {
        let csv = response.data
        var downloadLink = document.createElement("a")
        var blob = new Blob(["\ufeff", csv])
        var url = URL.createObjectURL(blob)
        downloadLink.href = url
        downloadLink.download = `${this.$route.params.uuid}.csv` //Name the file here
        document.body.appendChild(downloadLink)
        downloadLink.click()
        document.body.removeChild(downloadLink)
      })
    },
    onSaveExport(item) {
      Exports.retrieve(item.uuid).then(response => {
        let csv = response.data
        var downloadLink = document.createElement("a")
        var blob = new Blob(["\ufeff", csv])
        var url = URL.createObjectURL(blob)
        downloadLink.href = url
        downloadLink.download = `${this.$route.params.uuid}.csv` //Name the file here
        document.body.appendChild(downloadLink)
        downloadLink.click()
        document.body.removeChild(downloadLink)
      })
    },
    onRefreshExport(item) {
      Exports.refresh(item.uuid).then(_ => this.refresh())
    },
    onCancelExport(item) {
      Exports.cancel(item.uuid).then(_ => this.refresh())
    },
    onDeleteExport(item) {
      Exports.delete(item.uuid).then(_ => this.refresh())
    },
    onClone(){
      Predictions.clone(this.$route.params.uuid).then(response => {
        this.notify('Prediction cloned')
        this.$router.push({ name: 'predictionShow', params: { uuid: response.data.uuid } } )
      })
    },
    exportConfig(item) {
      let output = ""
      output += '<strong>Targets</strong>: ' + item.config.targets.join(',') + '; '
      output += '<strong>Methods</strong>: ' + item.config.methods.join(',') + '; '
      return output
    }
  },
  computed: {
    tab: {
      set (tab) {
        this.$router.replace({ hash: tab })
      },
      get () {
        return this.$route.hash.replace('#', '')
      }
    },
    result() {
      if ('result' in this.prediction) return this.prediction.result
    },
    status() {
      if (this.prediction.status == "SUCCESS") return "success--text"
      else if (this.prediction.status == "RUNNING") return "success--text"
      else if (this.prediction.status == "REVOKED") return "warning--text"
      else if (this.prediction.status == "PENDING") return "primary--text"
      else if (this.prediction.status == "FAILURE") return "error--text"
      else return ""
    },
    targets() {
      if (this.prediction) {
        let targets = []
        for (let [key, value] of Object.entries(this.prediction.config.targets)) {
          if (value != null) targets.push({ text: this.capitalize(key), value: key })
        }
        return targets
      }
      return []
    },
    methods() {
      if (this.prediction) {
        let methods = []
        this.prediction.methods.forEach(element => {
          methods.push({ text: element.name, value: element.id })
        })
        return methods
      }
      return []
    },
    errors() {
      if (this.result) {
        var result = JSON.parse(this.result.result)
        if (typeof result === "object" && result !== null)
          if (Array.isArray(result["exc_message"])) return `${result["exc_type"]}: ${result["exc_message"][0]}`
          else return result["exc_type"]
        return result
      }
      return null
    }
  }
}
</script>